import LegalLayout from './LegalLayout.jsx';

/**
 * Terms of Service — scoped to a private-beta B2B SaaS, not a consumer
 * platform. Short, plain-English, covers the bases Meta App Review cares
 * about (who owns what, acceptable use, termination, liability).
 */
export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="June 1, 2026">
      <section>
        <h2>1. The agreement</h2>
        <p>
          These Terms govern your use of Markaii (the "Service"), operated by DCLACorp
          ("we", "us"). By signing in, creating a project, or connecting a social
          account, you agree to these Terms. If you don't agree, don't use the Service.
        </p>
      </section>

      <section>
        <h2>2. Eligibility</h2>
        <p>
          The Service is currently in private beta and invite-only. You must be at
          least 18 years old and authorized to bind the organization (your company,
          your client, etc.) you sign up on behalf of.
        </p>
      </section>

      <section>
        <h2>3. Your account</h2>
        <ul>
          <li>You are responsible for keeping your password secure.</li>
          <li>You are responsible for activity under your account, including any social posts published while your account is connected.</li>
          <li>You must notify us immediately at <a href="mailto:support@markaii.com">support@markaii.com</a> if you suspect unauthorized access.</li>
        </ul>
      </section>

      <section>
        <h2>4. Acceptable use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Generate or publish content that's illegal, defamatory, harassing, fraudulent, or infringes someone else's intellectual property</li>
          <li>Generate spam, deceptive marketing, fake reviews, or content that violates the terms of any connected platform (Meta, LinkedIn, X, TikTok, etc.)</li>
          <li>Generate content intended to deceive about its AI origin where disclosure is legally required</li>
          <li>Attempt to reverse engineer, scrape, or load-test the Service</li>
          <li>Circumvent the quotas tied to your billing plan</li>
        </ul>
        <p>We may suspend or terminate accounts that breach this section.</p>
      </section>

      <section>
        <h2>5. Connected social accounts</h2>
        <p>
          When you connect a social account through OAuth, you grant the Service the
          permissions you authorized on the platform's consent screen — typically
          read profile, read engagement metrics, publish content. You can revoke
          access any time from the Markaii dashboard or directly on the platform's
          settings page. Disconnecting stops all future publishing and stats polling
          for that account.
        </p>
        <p>
          You remain responsible for the content published from your account. Markaii
          publishes drafts you have approved (manually or via auto-publish you have
          turned on); we do not pre-moderate the output of AI generation beyond the
          hard constraints you configured per brand.
        </p>
      </section>

      <section>
        <h2>6. Content ownership</h2>
        <ul>
          <li>You own the brand profiles, settings, and any custom prompts you put into the Service.</li>
          <li>You own the AI-generated drafts the Service produces from your inputs. You're responsible for whether they're fit for your use case and for any third-party rights in their output (e.g., trademark, likeness).</li>
          <li>You grant us a non-exclusive license to process your data and content as needed to operate the Service.</li>
          <li>We own the Service software, branding, and the marketing-loop methodology behind it.</li>
        </ul>
      </section>

      <section>
        <h2>7. Subscription, billing, refunds</h2>
        <p>
          Paid plans (when available outside the private beta) are billed in advance
          on a monthly basis via Stripe. You can cancel any time from your Billing
          page; cancellation takes effect at the end of the current period — no
          partial-month refunds.
        </p>
        <p>
          If you request a refund and we approve it at our discretion, the refund
          deducts the payment processor fee so the business breaks even by default —
          we don't absorb processor fees on refunds.
        </p>
      </section>

      <section>
        <h2>8. Quotas and rate limits</h2>
        <p>
          Each plan has limits on monthly posts, projects, connected accounts, and
          daily AI calls. We may rate-limit or 429 your requests if you exceed them.
          We may adjust quotas with 30 days' notice via email.
        </p>
      </section>

      <section>
        <h2>9. Service availability</h2>
        <p>
          We aim for high availability but don't guarantee 100% uptime. The Service
          may be temporarily unavailable for maintenance, upgrades, or external
          factors (platform API outages, AI provider outages). We don't compensate
          for outages during the private beta.
        </p>
      </section>

      <section>
        <h2>10. Warranties and disclaimers</h2>
        <p>
          The Service is provided "as is". AI-generated content may be inaccurate,
          biased, or unsuitable. You are responsible for reviewing every draft before
          approval (or for configuring auto-publish knowing the implications). We
          don't warrant that the Service will meet any specific marketing or revenue
          goals.
        </p>
      </section>

      <section>
        <h2>11. Limitation of liability</h2>
        <p>
          To the maximum extent permitted by law, our aggregate liability for any
          claim related to the Service is limited to the fees you paid us in the
          12 months preceding the claim. We are not liable for indirect, incidental,
          consequential, or punitive damages, including lost profits or lost data.
        </p>
      </section>

      <section>
        <h2>12. Indemnification</h2>
        <p>
          You agree to defend and indemnify us against third-party claims arising
          from content you published through the Service, your breach of these
          Terms, or your violation of any law or platform terms.
        </p>
      </section>

      <section>
        <h2>13. Termination</h2>
        <p>
          You can close your account any time from <a href="/account">Account</a> or
          by emailing <a href="mailto:support@markaii.com">support@markaii.com</a>.
          We may suspend or terminate accounts that breach these Terms, with notice
          when reasonable. On termination, your data is handled per the
          <a href="/privacy"> Privacy Policy</a>.
        </p>
      </section>

      <section>
        <h2>14. Governing law</h2>
        <p>
          These Terms are governed by the laws of the jurisdiction where DCLACorp is
          registered, without regard to conflict-of-law principles. Disputes will be
          resolved in the courts of that jurisdiction.
        </p>
      </section>

      <section>
        <h2>15. Changes to these Terms</h2>
        <p>
          We may update these Terms. Material changes are posted at the top of this
          page and emailed to account holders 30 days before they take effect.
          Continuing to use the Service after a change means you accept the new Terms.
        </p>
      </section>

      <section>
        <h2>16. Contact</h2>
        <p>
          <a href="mailto:support@markaii.com">support@markaii.com</a>
        </p>
      </section>
    </LegalLayout>
  );
}
